import { SHStack } from "@components/design-systems/sh-stack";
import { Controller, useFormContext } from "react-hook-form";
import { SHAutocomplete } from "@components/design-systems/sh-auto-complete";
import { FeatureTemplateSelectionDTO } from "@models/feature-template/entities/featureTemplate";
import { SHRadio } from "@components/design-systems/sh-radio";
import { SHTypography } from "@components/design-systems/sh-typography";
import { InformationButton } from "@components/buttons/information";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { isEmpty, take } from "lodash";
import { useState } from "react";
import { useEffectOnce } from "react-use";
import { getExistingFeatureTemplate } from "@services/feature-template/featureTemplateService";
import { AnalysisSetupStepConstants as Constants } from "@pages/platform-analysis-v2/_id/steps/setup/constants";

export const AnalysisSetupStepFeatureTemplateSelection = ({
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const { control } = useFormContext<AnalysisSetupDTO>();
  const [optionFeatureTemplates, setOptionFeatureTemplates] = useState<
    FeatureTemplateSelectionDTO[]
  >([]);
  const [initFeatureTemplates, setInitFeatureTemplates] = useState<
    FeatureTemplateSelectionDTO[]
  >([]);

  const loadFeatureTemplates = async (filterByFeatureTemplateName?: string) => {
    const { data } = await getExistingFeatureTemplate(
      filterByFeatureTemplateName,
    );
    return data ?? [];
  };

  useEffectOnce(() => {
    loadFeatureTemplates().then((data) => {
      setInitFeatureTemplates(data ?? []);
      setOptionFeatureTemplates(
        take(data, Constants.MaxTemplateItemToTake) ?? [],
      );
    });
  });

  return (
    <SHStack
      spacing={3}
      direction="column"
      sx={{ width: { xs: "100%", md: 520 } }}
    >
      <SHStack direction="column">
        <Controller
          name={"loadTemplateId"}
          control={control}
          render={({ field: { onChange } }) => (
            <SHAutocomplete
              disabled={disabled}
              textFieldProps={{
                InputLabelProps: { shrink: true },
                label: "Load feature selection from a saved template",
                placeholder: "Search for template",
              }}
              options={optionFeatureTemplates ?? []}
              getOptionLabel={(option: FeatureTemplateSelectionDTO) =>
                option?.name ?? ""
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option?.id} style={{ padding: "1px 10px" }}>
                  <SHStack alignItems={"center"} direction="row" spacing={1}>
                    <SHRadio checked={selected} />
                    <SHTypography variant="body3" fontWeight={500}>
                      {`${option.name} - ${option.selectedFeatures}/${option.totalFeatures} features selected`}
                    </SHTypography>
                    {option?.notes && (
                      <InformationButton content={option?.notes} />
                    )}
                  </SHStack>
                </li>
              )}
              sx={{ width: { xs: "100%", md: 520 } }}
              onChange={(_, value) => {
                onChange(value?.id);
                setOptionFeatureTemplates(initFeatureTemplates);
              }}
              onInputChange={(_, newValue) => {
                if (isEmpty(newValue)) {
                  setOptionFeatureTemplates(
                    take(
                      initFeatureTemplates,
                      Constants.MaxTemplateItemToTake,
                    ) ?? [],
                  );
                } else {
                  setOptionFeatureTemplates(initFeatureTemplates ?? []);
                }
              }}
            />
          )}
        />
      </SHStack>
    </SHStack>
  );
};
